import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import firebase from 'firebase/compat/app';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  from,
  Observable,
  of,
} from 'rxjs';
import { catchError, map, mergeMap, shareReplay, take } from 'rxjs/operators';
import {
  Company,
  CompanyV2Service,
  Provider,
} from '../services/company-v2.service';
import { CustomTokenService } from '../services/custom-token.service';
import { RoleService } from '../services/role.service';
import { UserService } from '../services/user.service';
import { Permissions, User } from '../shared/model/user';
import { shouldLogout } from '../shared/utils/auth.utils';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  public token$: Observable<string>;
  public authUser$: Observable<firebase.User>;
  public isLoggedIn$ = new Observable<boolean>();

  public loggedInUser$: Observable<User> = new Observable<User>();
  public companyUser!: User;

  private _permissions$: BehaviorSubject<Permissions> =
    new BehaviorSubject<Permissions>(new Permissions());
  public permissions$: Observable<Permissions> =
    this._permissions$.asObservable();
  public permissions: Permissions = new Permissions();

  public currentCompanyId!: string;

  private _currentCompany$: BehaviorSubject<Company> =
    new BehaviorSubject<Company>(null);
  public currentCompany$: Observable<Company> =
    this._currentCompany$.asObservable();

  private _currentCompanyId$: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);
  public currentCompanyId$: Observable<string> =
    this._currentCompanyId$.asObservable();

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private companyV2Service: CompanyV2Service,
    private customTokenService: CustomTokenService,
    private roleService: RoleService
  ) {
    this.token$ = this.currentCompanyId$.pipe(
      mergeMap((currentCompanyId) =>
        this.authService.getUser(currentCompanyId).pipe(
          mergeMap((user) => {
            if (!user) {
              return of(null);
            }

            return from(user.getIdTokenResult()).pipe(
              mergeMap((token) => {
                if (
                  shouldLogout(token, new Date(user.metadata.lastSignInTime!))
                ) {
                  Sentry.captureMessage(
                    `Old credentials for ${user.uid} and ${currentCompanyId}. Will logout`
                  );
                  if (currentCompanyId) {
                    this.authService.partialLogout(currentCompanyId);
                  } else {
                    this.authService.totalLogout();
                  }
                }

                return this.authService.getToken(currentCompanyId || null);
              })
            );
          })
        )
      )
    );

    this.authUser$ = this.currentCompanyId$.pipe(
      mergeMap((currentCompanyId) => this.authService.getUser(currentCompanyId))
    );

    this.isLoggedIn$ = this.authUser$.pipe(
      map((user) => !!user && !user.isAnonymous)
    );

    this.loggedInUser$ = this.authUser$.pipe(
      mergeMap((firebaseUser) => {
        const getUser = firebaseUser ? this.userService.getProfile() : of(null);
        return combineLatest([of(firebaseUser), getUser]);
      }),
      map(([firebaseUser, user]: [firebase.User, User]) => {
        if (user) {
          user.last_sign_in = firebaseUser.metadata.lastSignInTime!;
        }
        return user;
      }),
      shareReplay(1)
    );
  }

  changeCurrentCompany(companyId: string | null): Observable<boolean> {
    if (companyId === this.currentCompanyId) {
      return of(true);
    }
    return this.authService.isLoggedIn(companyId).pipe(
      take(1),
      mergeMap((isLoggedIn) => {
        if (isLoggedIn) {
          return this.updateCompanyData(companyId).pipe(take(1));
        }

        return this.automaticallySignin(companyId).pipe(take(1));
      })
    );
  }

  private updateCompanyData(companyId: string | null): Observable<boolean> {
    if (!companyId) {
      this._currentCompany$.next(null);
      this.currentCompanyId = null!;
      this._currentCompanyId$.next(null);
      this.permissions = new Permissions();
      this._permissions$.next(this.permissions);
      return of(true);
    }

    this._currentCompanyId$.next(companyId);
    this.currentCompanyId = companyId;
    return forkJoin({
      company: this.companyV2Service.getCompanyDetails(companyId),
      permissions: this.companyV2Service.getPermissions(companyId),
      user: this.userService.getCompleteProfile(companyId),
    }).pipe(
      mergeMap(({ company, permissions, user }) => {
        this._currentCompany$.next(company);
        this.companyUser = user;
        return this.roleService
          .getRole(user?.restrictions?.role?.code || '')
          .pipe(
            map((role) => {
              const permissionsSpicedb: Record<string, string> = {};

              role?.permissions?.forEach(
                (permission) => (permissionsSpicedb[permission] = 'company')
              );
              role?.documents?.reader?.resources?.forEach(
                (permission) =>
                  (permissionsSpicedb[`spicedb_${permission}_r`] = 'company')
              );
              role?.documents?.writer?.resources?.forEach(
                (permission) =>
                  (permissionsSpicedb[`spicedb_${permission}_w`] = 'company')
              );

              this.permissions = permissions;
              this.permissions.permissions = {
                ...permissions.permissions,
                ...permissionsSpicedb,
              };
              this._permissions$.next(this.permissions);
              return true;
            })
          );
      }),
      catchError((err) => {
        console.error(err);
        return of(false);
      })
    );
  }

  private customTokenSignin(company: Company): Observable<boolean> {
    return this.customTokenService
      .create(this.currentCompanyId, company.organization_id)
      .pipe(
        mergeMap((response) =>
          this.authService
            .signInWithCustomToken(
              company.id,
              company.providers[0].auth_tenant_id,
              response.token
            )
            .pipe(
              map((result) => !!result.user),
              catchError((error) => {
                console.error(error);
                return of(false);
              })
            )
        )
      );
  }

  private samlSignin(
    companyId: string,
    provider: Provider
  ): Observable<boolean> {
    return this.authService
      .signInWithRedirect(
        companyId,
        provider.auth_tenant_id,
        provider.id,
        false
      )
      .pipe(
        map(() => true),
        catchError((error) => {
          console.error(error);
          return of(false);
        })
      );
  }

  private automaticallySignin(companyId: string | null): Observable<boolean> {
    return this.authService.isLoggedIn(null).pipe(
      take(1),
      mergeMap((isLoggedIn) => {
        if (isLoggedIn) {
          // We must use default credentials to list companies
          this.updateCompanyData(null);
        }

        return this.companyV2Service.listCompanies().pipe(
          take(1),
          mergeMap((companies) => {
            const company = companies.items.find(
              (company) => company.id === companyId
            );
            if (!company) {
              return of(false);
            }

            return this.authUser$.pipe(
              take(1),
              mergeMap((authUser) => {
                if (!authUser?.uid) {
                  return of(false);
                }
                Sentry.setUser({ id: authUser.uid });
                const email = authUser.email ?? '';
                if (/@closecare.com.br$/.test(email)) {
                  return this.customTokenSignin(company).pipe(take(1));
                }

                const domain = email.split('@')[1];
                const provider = company.providers.find((provider) =>
                  provider.domains.includes(domain)
                );
                if (!provider) {
                  return of(false);
                }

                if (provider.type === 'saml') {
                  return this.samlSignin(companyId, provider).pipe(take(1));
                }

                return this.customTokenSignin(company).pipe(take(1));
              }),
              mergeMap((signedIn) =>
                signedIn
                  ? this.updateCompanyData(companyId).pipe(take(1))
                  : of(false)
              )
            );
          })
        );
      }),
      catchError((error) => {
        // Contain any failure in the auto sign-in chain (unguarded
        // listCompanies / null authUser) as a handled "not signed in" outcome
        // so it never errors the guard observable that subscribes here.
        Sentry.captureException(error, { tags: { flow: 'automatic_signin' } });
        return of(false);
      })
    );
  }
}
